/**
 * Bankame Widget — Integración con Shopify (SARANGO)
 * v3.4.0 — VACIADO DEL CARRITO tras el pago:
 *   • vaciarCarritoShopify() → POST a /cart/clear.js cuando el pago se
 *     confirma, sea cual sea el flujo (contado o cuotas).
 *   • Solo en modo carrito y solo si la orden llegó a crearse en Shopify:
 *     si create-pending-order falló, el carrito es el único registro de
 *     lo que el cliente quería comprar.
 *   • El redirect espera tanto a la creación de la orden como al vaciado.
 *
 * v3.3.0 — DIAGNÓSTICO del flujo AL CONTADO + fallback (se mantiene):
 *   • Sniffer de postMessage, espías sobre callbacks no manejados,
 *     introspección del SDK, payload completo y flowType en cada callback.
 *   • FALLBACK: si llega onPaymentConfirmed sin que se haya creado la
 *     orden (no hubo onClientConfirmed previo), la orden se crea en ese
 *     momento y recién después se marca como pagada.
 *   • obtenerDatosCliente entiende las claves del payload real de
 *     Bankame: client{firstname, lastname, phone_number, ...}.
 *
 * v3.2.0 — MODO CARRITO (se mantiene):
 *   • initCartWidget() → monta el widget en #bankame-cart-btn
 *   • obtenerDatosCarritoActual() → datos frescos desde /cart.js,
 *     enriquecidos con compare_at_price desde /products/{handle}.js
 *   • Callbacks compartidos entre producto y carrito.
 *
 * v3.1.0 — Fixes previos (se mantienen):
 *   • store_id / X-Store-Id en payment-confirmed, create-pending-order
 *     y link-order.
 *   • Logs centralizados + panel flotante con ?bankame_logs=1.
 */

(function () {
  "use strict";

  var config     = window.bankameConfig || {};
  var apiKey     = config.apiKey     || "";
  var widgetUrl  = config.widgetUrl  || "";
  var backendUrl = config.backendUrl || "";
  var storeId    = config.storeId    || "";

  // Modo activo: "product" | "cart". Se define en el punto de entrada
  // según el contenedor presente en la página.
  var modo = "product";

  // ═══════════════════════════════════════════════════════════════════
  // Logger centralizado (consola + localStorage + backend)
  // ═══════════════════════════════════════════════════════════════════
  var BANKAME_LOG_KEY       = "bankame_frontend_logs_" + storeId;
  var BANKAME_LOG_MAX_LOCAL = 400;
  var BANKAME_CTX_MAX_CHARS = 6000;   // recorta contextos gigantes

  function recortarContexto(context) {
    if (!context) return null;
    try {
      var s = JSON.stringify(context);
      if (s.length <= BANKAME_CTX_MAX_CHARS) return context;
      return { _truncado: true, _largo: s.length, _muestra: s.slice(0, BANKAME_CTX_MAX_CHARS) };
    } catch (e) {
      return { _noSerializable: String(context) };
    }
  }

  function bankameLog(level, message, context) {
    var entry = {
      timestamp: new Date().toISOString(),
      store_id:  storeId,
      source:    "widget-" + modo,
      level:     (level || "INFO").toUpperCase(),
      message:   String(message || ""),
      context:   recortarContexto(context),
      page_url:  window.location.href
    };
    try {
      var tag = "[Bankame:" + entry.level + "]";
      if (entry.level === "ERROR")                                 console.error(tag, entry.message, entry.context || "");
      else if (entry.level === "WARN" || entry.level === "WARNING") console.warn(tag, entry.message, entry.context || "");
      else                                                          console.log(tag, entry.message, entry.context || "");
    } catch (_) {}
    try {
      var stored = JSON.parse(localStorage.getItem(BANKAME_LOG_KEY) || "[]");
      stored.push(entry);
      while (stored.length > BANKAME_LOG_MAX_LOCAL) stored.shift();
      localStorage.setItem(BANKAME_LOG_KEY, JSON.stringify(stored));
    } catch (_) {}
    try {
      if (backendUrl) {
        fetch(backendUrl + "/api/bankame/log-event", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(entry),
          keepalive: true
        }).catch(function () {});
      }
    } catch (_) {}
  }
  window.bankameLog = bankameLog;

  // Volcado rápido desde la consola: bankameDump()
  window.bankameDump = function () {
    try {
      return JSON.parse(localStorage.getItem(BANKAME_LOG_KEY) || "[]");
    } catch (e) {
      return [];
    }
  };

  var productWidget = null;
  var cartWidget    = null;

  // Estado de la venta (equivalente a saleDataProducto del botón manual)
  var saleData = {
    saleId: null,
    shopifyOrderId: null,
    shopifyOrderNumber: null,
    shopifyOrderName: null,
    totalAmount: null,
    initialPaymentAmount: null,
    flowType: null,
    ordenPromesa: null,
    limpiezaPromesa: null
  };

  // ═══════════════════════════════════════════════════════════════════
  // DIAGNÓSTICO — helpers
  // ═══════════════════════════════════════════════════════════════════

  // Extrae el flujo declarado por el widget:
  //   'direct-payment' → pago AL CONTADO
  //   'installments'   → pago en cuotas
  function detectarFlowType(payload) {
    var p = payload || {};
    return p.flowType || p.flow_type || p.flujo || p.paymentType || p.payment_type || null;
  }

  function resumirPayload(payload) {
    var p = payload || {};
    var claves = [];
    try { claves = Object.keys(p); } catch (e) {}
    return {
      saleId:   p.saleId || p.id || p.sale_id || null,
      flowType: detectarFlowType(p),
      claves:   claves
    };
  }

  // Lista propiedades y métodos (incluye los del prototipo, que Object.keys omite)
  function listarMiembros(obj) {
    var out = [];
    if (!obj) return out;
    try {
      for (var k in obj) {
        out.push(k + ":" + typeof obj[k]);
      }
      var proto = Object.getPrototypeOf(obj);
      if (proto && proto !== Object.prototype) {
        Object.getOwnPropertyNames(proto).forEach(function (k) {
          if (out.indexOf(k + ":function") === -1 && k !== "constructor") {
            out.push(k + ":" + typeof obj[k]);
          }
        });
      }
    } catch (e) {}
    return out;
  }

  // Callbacks que NO manejamos pero que el SDK podría disparar.
  // Si alguno se usa en el flujo al contado, va a quedar en el log.
  var CALLBACKS_ESPIA = [
    "onSaleCreated", "onSaleConfirmed", "onSaleCompleted",
    "onCashPayment", "onCashConfirmed", "onDirectPayment", "onDirectPaymentConfirmed",
    "onSuccess", "onComplete", "onCompleted", "onFinish", "onFinished",
    "onOrderCreated", "onOrderConfirmed",
    "onPayment", "onPaymentPending", "onPaymentRejected", "onPaymentFailed",
    "onTransaction", "onTransactionConfirmed",
    "onStatusChange", "onStateChange", "onStep", "onChange", "onUpdate",
    "onQrGenerated", "onVerificationCode",
    "onOpen", "onReady", "onInit", "onClose", "onCancel", "onCancelled"
  ];

  function agregarEspias(opciones) {
    CALLBACKS_ESPIA.forEach(function (nombre) {
      if (typeof opciones[nombre] === "function") return; // no pisar los reales
      opciones[nombre] = function () {
        var args = [];
        try { args = Array.prototype.slice.call(arguments); } catch (e) {}
        bankameLog("WARN", "SDK disparó un callback NO manejado: " + nombre, {
          resumen: resumirPayload(args[0]),
          payload: args[0],
          argsExtra: args.length > 1 ? args.slice(1) : undefined
        });
      };
    });
    return opciones;
  }

  // Registra TODO lo que el iframe del widget manda por postMessage.
  // Es la red de seguridad: aunque el SDK no exponga un callback para
  // el flujo al contado, el mensaje del iframe igual queda logueado.
  function instalarSnifferPostMessage() {
    var origenWidget = "";
    try { origenWidget = new URL(widgetUrl).origin; } catch (e) {}

    window.addEventListener("message", function (ev) {
      try {
        // Solo mensajes del widget (evita ruido de apps de Shopify).
        if (origenWidget && ev.origin !== origenWidget) return;

        var data = ev.data;
        if (typeof data === "string") {
          try { data = JSON.parse(data); } catch (e) {}
        }

        bankameLog("INFO", "postMessage del widget", {
          origin: ev.origin,
          tipo:   (data && (data.type || data.event || data.action || data.name)) || typeof data,
          flowType: detectarFlowType(data) || detectarFlowType(data && data.payload),
          data:   data
        });
      } catch (e) {}
    }, false);

    bankameLog("INFO", "Sniffer de postMessage instalado", { origenEscuchado: origenWidget || "(todos)" });
  }

  // ── Retry hasta que el SDK esté disponible ─────────────────────────
  function initWithRetry(initFn, maxRetries) {
    var retries = 0;
    maxRetries  = maxRetries || 50;
    (function attempt() {
      if (window.BankameWidget) {
        initFn();
      } else if (retries < maxRetries) {
        retries++;
        requestAnimationFrame(attempt);
      } else {
        bankameLog("WARN", "El SDK no cargó después de varios intentos.");
      }
    })();
  }

  // ── Input de cantidad ──────────────────────────────────────────────
  function obtenerInputCantidad() {
    return document.querySelector('form[action*="/cart/add"] input[name="quantity"]')
        || document.querySelector('input[name="quantity"][form]')
        || document.querySelector('input[name="quantity"]');
  }

  // ── Variante seleccionada ──────────────────────────────────────────
  function obtenerVariantIdSeleccionada() {
    var el = document.querySelector('form[id^="product-form-"]:not([id*="installment"]) [name="id"]')
          || document.querySelector('form[action*="/cart/add"] [name="id"]')
          || document.querySelector('[name="id"]');
    return el ? el.value : null;
  }

  // ── DATOS FRESCOS DEL PRODUCTO ─────────────────────────────────────
  function obtenerDatosProductoActual(handle) {
    var variantId = obtenerVariantIdSeleccionada();
    if (!variantId) {
      return Promise.reject(new Error("No hay variante seleccionada"));
    }

    var qtyInput = obtenerInputCantidad();
    var quantity = qtyInput ? (parseInt(qtyInput.value) || 1) : 1;

    return fetch("/products/" + handle + ".js")
      .then(function (res) {
        if (!res.ok) throw new Error("Error al obtener datos del producto");
        return res.json();
      })
      .then(function (fullProductData) {
        var currentVariant = fullProductData.variants.find(function (v) {
          return String(v.id) === String(variantId);
        });
        if (!currentVariant) throw new Error("Variante no encontrada");

        if (!currentVariant.available) {
          throw new Error("Este producto está agotado y no está disponible para compra con Bankame");
        }

        var compareAtPrice = currentVariant.compare_at_price || null;
        var precioFinal;
        if (compareAtPrice && compareAtPrice > currentVariant.price) {
          precioFinal = compareAtPrice;
          bankameLog("INFO", "Usando precio TACHADO", { precio: compareAtPrice / 100 });
        } else {
          precioFinal = currentVariant.price;
          bankameLog("INFO", "Usando precio normal", { precio: currentVariant.price / 100 });
        }

        var producto = {
          variant_id:    String(variantId),
          product_id:    String(fullProductData.id),
          product_title: fullProductData.title,
          variant_title: currentVariant.title || "Default",
          quantity:      quantity,
          price:         (parseFloat(precioFinal) / 100).toFixed(2),
          line_price:    (parseFloat(precioFinal) / 100 * quantity).toFixed(2),
          sku:           currentVariant.sku || "",
          handle:        handle
        };

        bankameLog("INFO", "Datos del producto obtenidos", producto);
        return [producto];
      });
  }

  // ── DATOS FRESCOS DEL CARRITO ──────────────────────────────────────
  // /cart.js NO devuelve compare_at_price, así que se completa pidiendo
  // /products/{handle}.js una vez por producto único del carrito.
  // Devuelve { items, details }:
  //   items   → forma que espera create-pending-order (igual que producto)
  //   details → forma que espera el SDK del widget
  function obtenerDatosCarritoActual() {
    return fetch("/cart.js")
      .then(function (res) {
        if (!res.ok) throw new Error("Error al obtener el carrito");
        return res.json();
      })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) {
          throw new Error("El carrito está vacío");
        }

        var handles = [];
        cart.items.forEach(function (item) {
          if (item.handle && handles.indexOf(item.handle) === -1) handles.push(item.handle);
        });

        return Promise.all(
          handles.map(function (h) {
            return fetch("/products/" + h + ".js")
              .then(function (r) { return r.ok ? r.json() : null; })
              .catch(function () { return null; });
          })
        ).then(function (productos) {
          var comparePorVariante = {};
          productos.forEach(function (p) {
            if (!p || !p.variants) return;
            p.variants.forEach(function (v) {
              comparePorVariante[String(v.id)] = v.compare_at_price || null;
            });
          });
          return { cart: cart, comparePorVariante: comparePorVariante };
        });
      })
      .then(function (data) {
        var items   = [];
        var details = [];

        data.cart.items.forEach(function (item) {
          var variantId  = String(item.variant_id || item.id);
          var precioVenta = item.final_price;                        // unitario con descuentos de línea
          var compareAt   = data.comparePorVariante[variantId];
          var precioLista = (compareAt && compareAt > precioVenta) ? compareAt : precioVenta;

          items.push({
            variant_id:    variantId,
            product_id:    String(item.product_id),
            product_title: item.product_title || item.title,
            variant_title: item.variant_title || "Default",
            quantity:      item.quantity,
            price:         (parseFloat(precioLista) / 100).toFixed(2),
            line_price:    (parseFloat(precioLista) / 100 * item.quantity).toFixed(2),
            sku:           item.sku || "",
            handle:        item.handle || ""
          });

          details.push({
            product:       item.product_title || item.title,
            unitPrice:     Math.round(precioLista / 100),
            discountPrice: Math.round(precioVenta / 100),
            quantity:      item.quantity
          });
        });

        bankameLog("INFO", "Datos del carrito obtenidos", {
          lineas: items.length,
          total_price: data.cart.total_price / 100
        });

        return { items: items, details: details };
      });
  }

  // ── VACIAR EL CARRITO TRAS LA COMPRA ───────────────────────────────
  // Se llama desde onPaymentConfirmed, así que cubre los dos flujos
  // (contado y cuotas).
  // Dos guardas a propósito:
  //   1. Solo en modo carrito. En modo producto el carrito puede tener
  //      cosas ajenas a esta compra y borrarlo destruiría la selección
  //      del cliente.
  //   2. Solo si la orden existe en Shopify. Si create-pending-order
  //      falló, el carrito es el único registro de lo que se quería
  //      comprar: borrarlo dejaría al cliente sin nada.
  function vaciarCarritoShopify() {
    if (modo !== "cart") return Promise.resolve();

    if (!saleData.shopifyOrderId) {
      bankameLog("WARN", "No se vacía el carrito: la orden no se creó en Shopify", {
        flowType: saleData.flowType,
        saleId:   saleData.saleId
      });
      return Promise.resolve();
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

    return fetch(root + "cart/clear.js", {
      method:    "POST",
      headers:   { "Content-Type": "application/json" },
      keepalive: true
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          if (!r.ok) {
            bankameLog("ERROR", "cart/clear.js falló", { status: r.status, body: j });
            return;
          }
          bankameLog("INFO", "Carrito vaciado tras el pago", {
            item_count:       j.item_count,
            shopify_order_id: saleData.shopifyOrderId,
            flowType:         saleData.flowType
          });
        });
      })
      .catch(function (e) {
        bankameLog("ERROR", "Excepción al vaciar el carrito: " + e.message);
      });
  }

  function calcularTotales(productos, costoEnvio) {
    var subtotal = productos.reduce(function (sum, p) {
      return sum + parseFloat(p.line_price);
    }, 0);
    var envio = parseFloat(costoEnvio) || 0;
    var total = subtotal + envio;
    return {
      subtotal:     subtotal.toFixed(2),
      costoEnvio:   envio.toFixed(2),
      total:        total.toFixed(2),
      cuotaInicial: (total / 2).toFixed(2)
    };
  }

  function obtenerDatosCliente(payload, container) {
    var liquidCustomer = {};
    try {
      liquidCustomer = JSON.parse(container.getAttribute("data-customer")) || {};
    } catch (e) {
      liquidCustomer = {};
    }

    var p = payload || {};
    var wc = p.customer || p.client || p.cliente || {};
    var wcAddress = wc.address || wc.shippingAddress || {};
    // El payload real de Bankame trae shippingData anidado en algunos flujos.
    var sd = p.shippingData || p.shipping_data || {};

    // OJO: el payload de Bankame usa 'firstname'/'lastname'/'phone_number'
    // (todo junto y en snake_case). Sin estos fallbacks el cliente entraba
    // como "Cliente Bankame" con teléfono vacío.
    var firstName = p.shippingClientName     || sd.shippingClientName
                 || wc.firstName || wc.first_name || wc.firstname || wc.name
                 || liquidCustomer.first_name || "Cliente";
    var lastName  = p.shippingClientLastName || sd.shippingClientLastName
                 || wc.lastName  || wc.last_name || wc.lastname
                 || liquidCustomer.last_name || "Bankame";
    var email     = p.shippingEmail          || sd.shippingEmail
                 || wc.email || liquidCustomer.email || "";
    var phone     = p.shipingPhoneNumber || p.shippingPhoneNumber
                 || sd.shipingPhoneNumber || sd.shippingPhoneNumber
                 || wc.phone || wc.phone_number || wc.phoneNumber
                 || liquidCustomer.phone || "";
    var street    = p.shippingAddress        || sd.shippingAddress
                 || wcAddress.street || wcAddress.address1 || wc.address
                 || liquidCustomer.address1 || "";
    var city      = p.shippingCity           || sd.shippingCity
                 || wcAddress.city   || wc.city || liquidCustomer.city || "";
    var state     = p.shippingDepartment     || sd.shippingDepartment
                 || wcAddress.state  || wcAddress.province || wc.department
                 || liquidCustomer.province || "";

    var cliente = {
      email:      email,
      first_name: firstName,
      last_name:  lastName,
      phone:      phone,
      address: {
        street:  street,
        city:    city,
        state:   state,
        country: "BO"
      }
    };

    bankameLog("INFO", "Datos de cliente resueltos", {
      cliente: cliente,
      origen: {
        habiaCustomerEnPayload: !!(p.customer || p.client || p.cliente),
        habiaShippingData:      !!(p.shippingData || p.shipping_data),
        habiaCustomerLiquid:    Object.keys(liquidCustomer).length > 0
      }
    });

    return cliente;
  }

  // ═══════════════════════════════════════════════════════════════════
  // 1) Crear orden PENDIENTE en Shopify + vincularla a la venta Bankame
  // ═══════════════════════════════════════════════════════════════════
  function crearOrdenPendienteYVincular(payload, container, handle) {
    bankameLog("INFO", "Creando orden pendiente en Shopify", {
      modo:     modo,
      flowType: saleData.flowType,
      saleId:   saleData.saleId
    });

    var fuenteItems = (modo === "cart")
      ? obtenerDatosCarritoActual().then(function (r) { return r.items; })
      : obtenerDatosProductoActual(handle);

    return fuenteItems
      .then(function (productos) {
        var shippingMethod = (payload && (payload.shippingMethod || payload.shipping_method))
                          || "Por coordinar (Bankame Widget)";
        var shippingCost   = (payload && (payload.shippingCost || payload.shipping_cost)) || 0;

        var totales = calcularTotales(productos, shippingCost);
        saleData.totalAmount          = totales.total;
        saleData.initialPaymentAmount = totales.cuotaInicial;

        bankameLog("INFO", "Totales calculados", totales);

        var cliente = obtenerDatosCliente(payload, container);

        var orderData = {
          customer:               cliente,
          shipping_address:       cliente.address,
          store_id:               storeId,
          shipping_method:        shippingMethod,
          shipping_cost:          totales.costoEnvio,
          items:                  productos,
          total_amount:           totales.total,
          initial_payment_amount: totales.cuotaInicial
        };

        bankameLog("INFO", "create-pending-order → enviando", {
          store_id: storeId,
          flowType: saleData.flowType,
          total:    totales.total,
          items:    productos.length,
          body:     orderData
        });

        return fetch(backendUrl + "/api/bankame/create-pending-order", {
          method:  "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Store-Id":   storeId
          },
          body:    JSON.stringify(orderData)
        });
      })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (errorData) {
            bankameLog("ERROR", "create-pending-order falló", { status: response.status, body: errorData });
            throw new Error(errorData.error || ("Error " + response.status));
          });
        }
        return response.json();
      })
      .then(function (result) {
        bankameLog("INFO", "create-pending-order OK — Shopify order creada", {
          shopify_order_id:     result.shopify_order_id,
          shopify_order_number: result.shopify_order_number,
          shopify_order_name:   result.shopify_order_name,
          respuestaCompleta:    result
        });

        saleData.shopifyOrderId     = result.shopify_order_id;
        saleData.shopifyOrderNumber = result.shopify_order_number;
        saleData.shopifyOrderName   = result.shopify_order_name;

        if (result.order_status_url || result.order_url) {
          window._bankameOrderUrl = result.order_status_url || result.order_url;
        }

        return vincularOrdenConBankame(result.shopify_order_id, saleData.saleId);
      });
  }

  // ── Vincular orden de Shopify con la venta de Bankame ─────────────
  function vincularOrdenConBankame(shopifyOrderId, bankameSaleId) {
    bankameLog("INFO", "link-order → enviando", {
      shopify_order_id: shopifyOrderId,
      bankame_sale_id:  bankameSaleId,
      store_id:         storeId
    });

    if (!shopifyOrderId || !bankameSaleId) {
      bankameLog("ERROR", "link-order abortado: shopify_order_id o bankame_sale_id vacío", {
        shopifyOrderId: shopifyOrderId,
        bankameSaleId:  bankameSaleId,
        flowType:       saleData.flowType
      });
      return Promise.resolve();
    }

    return fetch(backendUrl + "/api/bankame/link-order", {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Store-Id":   storeId
      },
      body:    JSON.stringify({
        shopify_order_id: shopifyOrderId,
        bankame_sale_id:  bankameSaleId,
        store_id:         storeId
      })
    })
      .then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (j) {
          if (!response.ok || j.success === false) {
            bankameLog("ERROR", "link-order respondió error", { status: response.status, body: j });
          } else {
            bankameLog("INFO", "link-order OK", j);
          }
        });
      })
      .catch(function (error) {
        bankameLog("ERROR", "link-order excepción: " + error.message);
      });
  }

  // ═══════════════════════════════════════════════════════════════════
  // 2) Marcar la orden como PAGADA
  // ═══════════════════════════════════════════════════════════════════
  function notificarPagoConfirmado(bankameTransactionId) {
    var requestBody = {
      bankame_transaction_id: bankameTransactionId,
      store_id:               storeId,
      shopify_order_id:       saleData.shopifyOrderId,
      total_amount:           saleData.totalAmount,
      initial_payment_amount: saleData.initialPaymentAmount,
      payment_date:           new Date().toISOString(),
      payment_method:         "QR",
      payment_reference:      "QR-" + Date.now()
    };

    // Señal clave para el caso "no se registra el pedido": si esto sale
    // en null, la orden nunca se creó y el backend no tiene qué marcar.
    if (!saleData.shopifyOrderId) {
      bankameLog("ERROR", "payment-confirmed SIN shopify_order_id — la orden no existe en Shopify", {
        flowType: saleData.flowType,
        saleId:   bankameTransactionId,
        saleData: saleData
      });
    }

    bankameLog("INFO", "payment-confirmed → enviando", {
      flowType: saleData.flowType,
      body:     requestBody
    });

    return fetch(backendUrl + "/api/bankame/payment-confirmed", {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Store-Id":   storeId
      },
      body:    JSON.stringify(requestBody)
    })
      .then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (data) {
          if (!response.ok || data.success === false) {
            bankameLog("WARN", "payment-confirmed respondió error (webhook debería resolver)", {
              status: response.status,
              body:   data
            });
            return data;
          }
          bankameLog("INFO", "payment-confirmed OK", data);
          return data;
        });
      })
      .catch(function (error) {
        bankameLog("WARN", "payment-confirmed excepción (webhook debería resolver): " + error.message);
      });
  }

  // ═══════════════════════════════════════════════════════════════════
  // Callbacks del SDK — compartidos entre producto y carrito
  // ═══════════════════════════════════════════════════════════════════
  function construirCallbacks(container, handle) {
    return {
      onClientConfirmed: function (payload) {
        bankameLog("INFO", "SDK onClientConfirmed", resumirPayload(payload));
        bankameLog("INFO", "Payload COMPLETO del widget (onClientConfirmed)", payload);

        var currentSaleId = (payload && (payload.saleId || payload.id)) || null;

        if (currentSaleId && window._bankameOrderCreatedForSaleId === currentSaleId) {
          bankameLog("INFO", "Orden ya creada para saleId — ignorando duplicado", { saleId: currentSaleId });
          return;
        }

        window._bankameOrderCreatedForSaleId = currentSaleId;
        window._bankamePaymentConfirmedFor   = null;
        window._bankameOrderUrl              = null;
        if (redirectTimer) { clearTimeout(redirectTimer); redirectTimer = null; }
        pendingRedirectUrl                   = null;

        saleData.saleId               = currentSaleId;
        saleData.shopifyOrderId       = null;
        saleData.shopifyOrderNumber   = null;
        saleData.shopifyOrderName     = null;
        saleData.totalAmount          = null;
        saleData.initialPaymentAmount = null;
        saleData.flowType             = detectarFlowType(payload);
        saleData.ordenPromesa         = null;
        saleData.limpiezaPromesa      = null;

        saleData.ordenPromesa = crearOrdenPendienteYVincular(payload, container, handle)
          .catch(function (error) {
            bankameLog("ERROR", "Error al crear la orden pendiente: " + error.message);
            window._bankameOrderCreatedForSaleId = null;
            window._bankameOrderUrl = (config.shopUrl || "/") + "?bankame_sale=" + (saleData.saleId || "");
          });
      },

      onPaymentConfirmed: function (payload) {
        bankameLog("INFO", "SDK onPaymentConfirmed", resumirPayload(payload));
        bankameLog("INFO", "Payload COMPLETO del widget (onPaymentConfirmed)", payload);

        var transactionId = (payload && (payload.saleId || payload.id)) || saleData.saleId;
        var flowType      = detectarFlowType(payload) || saleData.flowType;
        saleData.flowType = flowType;

        if (transactionId && window._bankamePaymentConfirmedFor === transactionId) {
          bankameLog("INFO", "Pago ya confirmado para saleId — ignorando duplicado", { saleId: transactionId });
          return;
        }
        window._bankamePaymentConfirmedFor = transactionId;

        var marcarPagada = function () {
          if (!transactionId) {
            bankameLog("ERROR", "No hay saleId para confirmar el pago", { payload: payload });
            return;
          }
          return notificarPagoConfirmado(transactionId).catch(function (error) {
            bankameLog("ERROR", "Error al actualizar Shopify como pagada: " + error.message);
          });
        };

        if (saleData.ordenPromesa) {
          bankameLog("INFO", "Hay orden en curso — se espera antes de marcar pagada", {
            flowType: flowType,
            saleId:   transactionId
          });
          saleData.ordenPromesa.then(marcarPagada, marcarPagada);
        } else {
          // ─────────────────────────────────────────────────────────
          // FALLBACK — flujo AL CONTADO (flowType 'direct-payment')
          // Si el widget confirma el pago sin haber pasado por
          // onClientConfirmed, la orden pendiente NUNCA se creó: por eso
          // esas compras no aparecen en Shopify. Se crea acá y recién
          // después se marca como pagada.
          // ─────────────────────────────────────────────────────────
          bankameLog("WARN", "onPaymentConfirmed SIN orden previa — creando la orden ahora (probable flujo al contado)", {
            flowType: flowType,
            saleId:   transactionId,
            modo:     modo
          });

          saleData.saleId                      = saleData.saleId || transactionId;
          window._bankameOrderCreatedForSaleId = transactionId;

          saleData.ordenPromesa = crearOrdenPendienteYVincular(payload, container, handle)
            .catch(function (error) {
              bankameLog("ERROR", "No se pudo crear la orden en el flujo al contado: " + error.message);
              window._bankameOrderCreatedForSaleId = null;
            });

          saleData.ordenPromesa.then(marcarPagada, marcarPagada);
        }

        // ── Vaciado del carrito ─────────────────────────────────────
        // Encadenado a la orden: crearOrdenPendienteYVincular lee
        // /cart.js, así que borrar antes dejaría la orden vacía.
        // Vale para los dos flujos porque este callback es el único
        // punto por el que pasan ambos.
        saleData.limpiezaPromesa = saleData.ordenPromesa
          ? saleData.ordenPromesa.then(vaciarCarritoShopify, vaciarCarritoShopify)
          : vaciarCarritoShopify();

        if (pendingRedirectUrl) return;
        var url = (config.shopUrl || "/") + "?bankame_sale=" + (transactionId || "");
        if (window._bankameOrderUrl) url = window._bankameOrderUrl;
        esperarCierreYRedirigir(url);
      },

      onError: function (error) {
        bankameLog("ERROR", "SDK reportó error: " + (error && error.message), {
          error:    error,
          flowType: saleData.flowType,
          saleData: saleData
        });
      }
    };
  }

  // ── PRODUCTO INDIVIDUAL ────────────────────────────────────────────
  function initProductWidget() {
    var container = document.getElementById("bankame-product-btn");
    if (!container) return;

    var handle = container.getAttribute("data-handle") || "";

    var details = [];
    try {
      details = JSON.parse(container.getAttribute("data-product")) || [];
    } catch (e) {
      bankameLog("ERROR", "Error al leer data-product: " + e.message);
      return;
    }

    var preciosPorVariante = {};
    try {
      preciosPorVariante = JSON.parse(container.getAttribute("data-variants")) || {};
    } catch (e) {
      preciosPorVariante = {};
    }

    bankameLog("INFO", "Details enviados al widget (init)", details);

    var opciones = agregarEspias(construirCallbacks(container, handle));
    opciones.widgetUrl = widgetUrl;
    opciones.apiKey    = apiKey;
    opciones.details   = details;
    opciones.target    = "#bankame-product-btn";

    productWidget = window.BankameWidget.init(opciones);

    bankameLog("INFO", "SDK inicializado (producto)", {
      instancia: listarMiembros(productWidget),
      global:    listarMiembros(window.BankameWidget)
    });

    // Polling de cantidad y variante
    var lastQty       = 1;
    var lastVariantId = obtenerVariantIdSeleccionada();
    setInterval(function () {
      if (!productWidget) return;

      var cambio = false;

      var qtyInput = obtenerInputCantidad();
      if (qtyInput) {
        var qty = parseInt(qtyInput.value) || 1;
        if (qty !== lastQty) {
          lastQty = qty;
          details[0].quantity = qty;
          cambio = true;
          bankameLog("INFO", "Cantidad actualizada", { qty: qty });
        }
      }

      var variantId = obtenerVariantIdSeleccionada();
      if (variantId && variantId !== lastVariantId) {
        lastVariantId = variantId;
        var precioVar = preciosPorVariante[variantId];
        if (precioVar) {
          container.style.display = precioVar.available === false ? "none" : "";

          if (precioVar.unitPrice !== details[0].unitPrice || precioVar.discountPrice !== details[0].discountPrice) {
            details[0].unitPrice     = precioVar.unitPrice;
            details[0].discountPrice = precioVar.discountPrice;
            cambio = true;
            bankameLog("INFO", "Precio actualizado por variante", {
              variantId: variantId,
              unitPrice: precioVar.unitPrice,
              discountPrice: precioVar.discountPrice
            });
          }
        }
      }

      if (cambio) {
        if (typeof productWidget.updateDetails === "function") {
          productWidget.updateDetails(JSON.parse(JSON.stringify(details)));
        } else {
          bankameLog("WARN", "El SDK no expone updateDetails — datos frescos se envían al confirmar.");
        }
      }
    }, 300);
  }

  // ── CARRITO ────────────────────────────────────────────────────────
  function initCartWidget() {
    var container = document.getElementById("bankame-cart-btn");
    if (!container) {
      bankameLog("WARN", "No se encontró #bankame-cart-btn — el widget de carrito no se monta.");
      return;
    }

    var details = [];
    try {
      details = JSON.parse(container.getAttribute("data-product")) || [];
    } catch (e) {
      bankameLog("ERROR", "Error al leer data-product del carrito: " + e.message);
      return;
    }

    if (details.length === 0) {
      bankameLog("WARN", "Carrito sin ítems al cargar — no se monta el widget.");
      return;
    }

    bankameLog("INFO", "Details enviados al widget (init carrito)", details);

    var opciones = agregarEspias(construirCallbacks(container, null));
    opciones.widgetUrl = widgetUrl;
    opciones.apiKey    = apiKey;
    opciones.details   = details;
    opciones.target    = "#bankame-cart-btn";

    cartWidget = window.BankameWidget.init(opciones);

    bankameLog("INFO", "SDK inicializado (carrito)", {
      instancia: listarMiembros(cartWidget),
      global:    listarMiembros(window.BankameWidget)
    });

    // ── Re-sincronización cuando cambia el carrito ───────────────────
    // El div vive FUERA de .js-contents, así que Dawn no lo re-renderiza:
    // los details hay que actualizarlos a mano.
    var refrescoPendiente = null;
    function refrescarDetallesCarrito() {
      // Tras el pago el carrito queda vacío a propósito: no hay que
      // reaccionar a ese cambio ni ocultar el widget mientras el cliente
      // todavía ve la confirmación.
      if (saleData.limpiezaPromesa) return;

      if (refrescoPendiente) clearTimeout(refrescoPendiente);
      refrescoPendiente = setTimeout(function () {
        refrescoPendiente = null;
        if (!cartWidget) return;

        obtenerDatosCarritoActual()
          .then(function (r) {
            container.style.display = r.details.length ? "" : "none";

            details.length = 0;
            r.details.forEach(function (d) { details.push(d); });

            if (typeof cartWidget.updateDetails === "function") {
              cartWidget.updateDetails(JSON.parse(JSON.stringify(details)));
              bankameLog("INFO", "Details del carrito actualizados", { lineas: details.length });
            } else {
              bankameLog("WARN", "El SDK no expone updateDetails — el monto se recalcula al confirmar.");
            }
          })
          .catch(function (error) {
            // Carrito vacío entra por acá: se oculta el widget.
            container.style.display = "none";
            bankameLog("WARN", "No se pudo refrescar el carrito: " + error.message);
          });
      }, 250);
    }

    // Evento propio disparado desde main-cart-footer.liquid
    document.addEventListener("bankame:cart-updated", refrescarDetallesCarrito);

    // PUB_SUB nativo de Dawn (por si el script del footer no está)
    try {
      if (typeof subscribe === "function" && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        subscribe(window.PUB_SUB_EVENTS.cartUpdate, refrescarDetallesCarrito);
      }
    } catch (e) {
      bankameLog("WARN", "No se pudo suscribir a PUB_SUB_EVENTS.cartUpdate: " + e.message);
    }
  }

  // ── ESPERAR CIERRE DEL POPUP PARA REDIRIGIR ────────────────────────
  var pendingRedirectUrl = null;
  var redirectTimer      = null;

  function esperarCierreYRedirigir(orderUrl) {
    if (redirectTimer) clearTimeout(redirectTimer);
    pendingRedirectUrl = orderUrl;
    redirectTimer = setTimeout(function () {
      var irA = function () {
        var url = window._bankameOrderUrl || pendingRedirectUrl;
        pendingRedirectUrl = null;
        redirectTimer     = null;
        bankameLog("INFO", "Redirigiendo tras el pago", { url: url, saleData: saleData });
        if (url) window.location.href = url;
      };

      // En el flujo al contado la orden se crea recién en
      // onPaymentConfirmed, y el vaciado del carrito va detrás: no
      // redirigir hasta que ambos terminen, o se cortan a mitad.
      var pendientes = [];
      if (saleData.ordenPromesa)    pendientes.push(saleData.ordenPromesa);
      if (saleData.limpiezaPromesa) pendientes.push(saleData.limpiezaPromesa);

      if (pendientes.length) {
        Promise.all(
          pendientes.map(function (p) { return p.catch(function () {}); })
        ).then(irA, irA);
      } else {
        irA();
      }
    }, 4000);
  }

  window.addEventListener("beforeunload", function () {
    if (productWidget) productWidget.destroy();
    if (cartWidget)    cartWidget.destroy();
  });

  // ═══════════════════════════════════════════════════════════════════
  // Panel flotante de logs (activar con ?bankame_logs=1)
  // ═══════════════════════════════════════════════════════════════════
  function renderBankameLogsPanel() {
    var url = new URL(window.location.href);
    var localOn    = url.searchParams.get("bankame_logs") === "1";
    var shortcutOn = localStorage.getItem("bankame_show_logs") === "1";
    if (!localOn && !shortcutOn) return;
    if (document.getElementById("bankameLogsPanel")) return;

    var panel = document.createElement("div");
    panel.id = "bankameLogsPanel";
    panel.style.cssText = "position:fixed;bottom:12px;right:12px;width:520px;max-width:95vw;height:360px;max-height:70vh;background:#0d1117;color:#e6edf3;border:1px solid #30363d;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,.4);z-index:2147483647;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;display:flex;flex-direction:column;overflow:hidden;";

    var header = document.createElement("div");
    header.style.cssText = "padding:8px 12px;background:#161b22;border-bottom:1px solid #30363d;display:flex;justify-content:space-between;align-items:center;font-weight:600;";
    header.innerHTML = "<span>📋 Bankame Logs — " + storeId + " / widget-" + modo + "</span>";

    var btns = document.createElement("div");
    btns.style.cssText = "display:flex;gap:6px;";
    var mkBtn = function (t, on) {
      var b = document.createElement("button");
      b.textContent = t;
      b.style.cssText = "background:#21262d;color:#e6edf3;border:1px solid #30363d;padding:3px 8px;border-radius:4px;cursor:pointer;font-size:11px;";
      b.onclick = on;
      return b;
    };
    btns.appendChild(mkBtn("🔄 Refrescar", function () { renderList(); }));
    btns.appendChild(mkBtn("🌐 Backend", function () {
      fetch(backendUrl + "/api/bankame/logs?store_id=" + storeId + "&limit=100")
        .then(function (r) { return r.json(); })
        .then(function (j) { renderList((j.logs || []).slice().reverse(), "backend"); })
        .catch(function (e) { alert("Error backend: " + e.message); });
    }));
    btns.appendChild(mkBtn("📋 Copiar", function () {
      try {
        var txt = localStorage.getItem(BANKAME_LOG_KEY) || "[]";
        navigator.clipboard.writeText(txt);
        alert("Logs copiados al portapapeles (" + JSON.parse(txt).length + " entradas)");
      } catch (e) { alert("No se pudo copiar: " + e.message); }
    }));
    btns.appendChild(mkBtn("🗑 Limpiar", function () { localStorage.removeItem(BANKAME_LOG_KEY); renderList(); }));
    btns.appendChild(mkBtn("✕", function () { panel.remove(); localStorage.removeItem("bankame_show_logs"); }));
    header.appendChild(btns);
    panel.appendChild(header);

    var list = document.createElement("div");
    list.style.cssText = "flex:1;overflow-y:auto;padding:8px 12px;";
    panel.appendChild(list);
    document.body.appendChild(panel);

    function renderList(externalLogs, source) {
      var logs = externalLogs || JSON.parse(localStorage.getItem(BANKAME_LOG_KEY) || "[]").slice().reverse();
      if (logs.length === 0) {
        list.innerHTML = '<em style="color:#8b949e;">Sin logs ' + (source ? "(" + source + ")" : "(local)") + "</em>";
        return;
      }
      list.innerHTML = "";
      logs.forEach(function (e) {
        var row = document.createElement("div");
        var colors = { ERROR: "#f85149", WARN: "#d29922", WARNING: "#d29922", INFO: "#58a6ff", DEBUG: "#8b949e" };
        var c = colors[e.level] || "#e6edf3";
        row.style.cssText = "padding:5px 0;border-bottom:1px solid #21262d;";
        var ctx = e.context ? '<div style="color:#8b949e;font-size:11px;margin-top:2px;white-space:pre-wrap;">' + JSON.stringify(e.context) + "</div>" : "";
        row.innerHTML = '<div><span style="color:#8b949e;">' + (e.timestamp || "") + '</span> <b style="color:' + c + ';">[' + (e.level || "INFO") + "]</b> " + (e.message || "") + "</div>" + ctx;
        list.appendChild(row);
      });
    }
    renderList();
  }
  window.mostrarBankameLogs = function () {
    localStorage.setItem("bankame_show_logs", "1");
    renderBankameLogsPanel();
  };

  // ── Punto de entrada ───────────────────────────────────────────────
  document.addEventListener("DOMContentLoaded", function () {
    if (!apiKey || !widgetUrl) {
      bankameLog("WARN", "API Key o Widget URL no configurados.");
      return;
    }
    if (!backendUrl || !storeId || storeId === "NUEVO_STORE_ID") {
      bankameLog("WARN", "backendUrl o storeId no configurados — las órdenes NO se crearán en Shopify.");
    }

    // El modo se decide por el contenedor presente, no solo por el flag:
    // así un isProduct desactualizado en caché no rompe la página.
    if (document.getElementById("bankame-cart-btn") && config.isProduct !== "yes") {
      modo = "cart";
    } else {
      modo = "product";
    }

    bankameLog("INFO", "Bankame widget iniciado", {
      version:   "3.4.0",
      storeId:   storeId,
      modo:      modo,
      isProduct: config.isProduct
    });

    instalarSnifferPostMessage();
    renderBankameLogsPanel();

    if (modo === "cart") {
      initWithRetry(initCartWidget);
    } else {
      initWithRetry(initProductWidget);
    }
  });

})();